<template>
  <div class="order-on-behalf-page">
    <!-- 头部 -->
    <div class="header">
      <div class="header-title">{{ $t('wholesaler.OrderOnBehalf.title') }}</div>
    </div>
    
    <!-- 页面内容 -->
    <div class="content">
      <!-- 第一步：选择档口 -->
      <div class="section">
        <div class="section-title">
          {{ $t('wholesaler.OrderOnBehalf.step1SelectStall') }}
          <button class="create-stall-btn" @click.stop="showCreateStallDialog = true">+ {{ $t('wholesaler.OrderOnBehalf.createNewStall') }}</button>
        </div>
        
        <!-- 搜索筛选区 -->
        <div class="filter-section" style="margin-bottom: 16px;">
          <!-- 搜索框 -->
          <div class="search-box">
            <input
              v-model="customerSearch"
              type="text"
              :placeholder="$t('wholesaler.OrderOnBehalf.searchStallPlaceholder')"
              @input="onSearchInput"
              class="search-input"
            />
          </div>
          
          <!-- 区域 + 按钮 -->
          <div class="filter-row">
            <select v-model="filterRegion" @change="onFilterChange" class="filter-select">
              <option value="">{{ $t('wholesaler.OrderOnBehalf.allRegions') }}</option>
              <option v-for="region in regions" :key="region.id || region.regionCode" :value="region.regionName">{{ region.regionName }}</option>
            </select>
            <button class="reset-btn" @click="resetFilters" style="flex-shrink:0; flex:none; padding:8px 16px;">{{ $t('wholesaler.OrderOnBehalf.reset') }}</button>
          </div>
        </div>
        
        <!-- 客户列表（单选） -->
        <div class="customer-list">
          <div
            v-for="customer in displayedCustomers"
            :key="'customer-' + customer.id"
            :class="['customer-selector', { selected: selectedCustomer && selectedCustomer.id === customer.id }]"
            @click="selectCustomer(customer)"
          >
            <div class="customer-name">{{ customer.shopName || customer.customerName || customer.name }}</div>
            <div class="customer-info">
              <span>📞 {{ customer.contactPerson || customer.phone }}</span>
              <span v-if="customer.regionName || customer.region"> · 📍 {{ customer.regionName || customer.region }}</span>
            </div>
          </div>
          
          <div v-if="displayedCustomers.length === 0 && !customerLoading" class="empty-state">
            {{ $t('wholesaler.OrderOnBehalf.noCustomers') }}
          </div>
        </div>
        
        <!-- 分页 -->
        <div v-if="customerTotalPages > 1" class="pagination-bar">
          <button class="page-btn" :disabled="customerPage <= 1" @click="goCustomerPage(customerPage - 1)">‹ {{ $t('wholesaler.OrderOnBehalf.prevPage') }}</button>
          <span class="page-info">{{ customerPage }} / {{ customerTotalPages }} {{ $t('wholesaler.OrderOnBehalf.pageUnit') }}, {{ $t('wholesaler.OrderOnBehalf.totalUnit') }} {{ customerTotal }} {{ $t('wholesaler.OrderOnBehalf.itemUnit') }}</span>
          <button class="page-btn" :disabled="customerPage >= customerTotalPages" @click="goCustomerPage(customerPage + 1)">{{ $t('wholesaler.OrderOnBehalf.nextPage') }} ›</button>
        </div>
      </div>

      <!-- 创建档口弹窗 -->
      <div v-if="showCreateStallDialog" class="dialog-overlay" @click.self="showCreateStallDialog = false">
        <div class="dialog-box">
          <div class="dialog-title">{{ $t('wholesaler.OrderOnBehalf.createNewStall') }}</div>
          <div class="dialog-content">
            <input
              v-model="newStallName"
              type="text"
              :placeholder="$t('wholesaler.OrderOnBehalf.enterStallNamePlaceholder')"
              class="dialog-input"
              @keyup.enter="createStall"
              ref="stallNameInput"
            />
          </div>
          <div class="dialog-actions">
            <button class="dialog-cancel-btn" @click="showCreateStallDialog = false">{{ $t('wholesaler.OrderOnBehalf.cancel') }}</button>
            <button class="dialog-confirm-btn" @click="createStall" :disabled="!newStallName.trim() || creatingStall">
              {{ creatingStall ? $t('wholesaler.OrderOnBehalf.creating') : $t('wholesaler.OrderOnBehalf.confirm') }}
            </button>
          </div>
        </div>
      </div>
      
      <!-- 第二步：选择商品（选完档口后才显示） -->
      <div class="section" v-if="selectedCustomer">
        <div class="section-title">{{ $t('wholesaler.OrderOnBehalf.step2SelectProducts') }}</div>
        
        <!-- 商品搜索 -->
        <div class="search-box" style="margin-bottom: 16px; position: relative;">
          <input
            v-model="productSearch"
            type="text"
            :placeholder="$t('wholesaler.OrderOnBehalf.searchProductPlaceholder')"
            @input="onProductSearch"
            @focus="onProductSearch"
            class="search-input"
          />
          <!-- 搜索下拉提示 -->
          <div v-if="showProductDropdown && productSearchResults.length > 0" class="product-dropdown">
            <div
              v-for="p in productSearchResults"
              :key="'search-' + p.id"
              class="product-option"
              @click="selectSearchProduct(p)"
            >
              <span class="option-name">{{ p.name }}</span>
              <span class="option-sku">{{ p.sku || '-' }}</span>
            </div>
          </div>
        </div>
        
        <!-- 已选商品区域 -->
        <div v-if="selectedProducts.length > 0" class="selected-products-section">
          <div class="selected-title">{{ $t('wholesaler.OrderOnBehalf.selectedProducts') }} ({{ selectedProducts.length }})</div>
          <div class="selected-products-list">
            <div v-for="product in selectedProducts" :key="'selected-' + product.id" class="selected-product-item">
              <div class="selected-product-info">
                <span class="selected-product-name">{{ product.name }}</span>
                <span class="selected-product-price">S$ {{ (prices[product.id] || 0).toFixed(2) }} × {{ quantities[product.id] || 0 }}{{ product.unit || 'kg' }}</span>
              </div>
              <button class="btn-remove-selected" @click="toggleProductSelection(product)">✕</button>
            </div>
          </div>
        </div>
        
        <!-- 加载状态 -->
        <div v-if="productsLoading" class="loading-state">
          <div class="loading-spinner">⏳</div>
          <p>{{ $t('wholesaler.OrderOnBehalf.loadingProducts') }}</p>
        </div>
        
        <!-- 商品列表（带多选框，显示批发价） -->
        <div v-else class="product-list">
          <div v-for="product in displayedProducts" :key="'product-' + product.id" class="product-item">
            <div class="product-item-top">
              <input 
                type="checkbox" 
                :checked="selectedProductIds.includes(product.id)"
                @change="toggleProductSelection(product)"
                class="product-checkbox"
              />
              <div class="product-emoji">{{ product.emoji || '📦' }}</div>
              <div class="product-name-full">{{ product.name }}</div>
            </div>
            <div class="product-item-bottom">
              <div class="price-ref-line">{{ $t('wholesaler.OrderOnBehalf.referencePrice') }}: S$ {{ ((product.wholesalePrice || product.minWholesalePrice || product.marketPrice) || 0).toFixed(2) }}</div>
              <div class="price-input-wrap" @click="openPricePicker(product)" :class="{ disabled: !selectedProductIds.includes(product.id) }">
                <span class="price-prefix">S$</span>
                <span class="price-display">{{ prices[product.id] ? prices[product.id].toFixed(2) : '0.00' }}</span>
                <span class="edit-arrow">▾</span>
              </div>
              <div class="quantity-display-wrap" @click="openQuantityPicker(product)" :class="{ disabled: !selectedProductIds.includes(product.id) }">
                <span class="quantity-display">{{ quantities[product.id] || 0 }}</span>
                <span class="unit">{{ product.unit || 'kg' }}</span>
                <span class="edit-arrow">▾</span>
              </div>
            </div>
          </div>
          
          <div v-if="displayedProducts.length === 0" class="empty-state">
            {{ $t('wholesaler.OrderOnBehalf.noProducts') }}
          </div>
        </div>
        
        <!-- 商品分页 -->
        <div v-if="productTotalPages > 1" class="pagination-bar">
          <button class="page-btn" :disabled="productPage <= 0" @click="goProductPage(productPage - 1)">‹ {{ $t('wholesaler.OrderOnBehalf.prevPage') }}</button>
          <span class="page-info">{{ productPage + 1 }} / {{ productTotalPages }} {{ $t('wholesaler.OrderOnBehalf.pageUnit') }}, {{ $t('wholesaler.OrderOnBehalf.totalUnit') }} {{ productTotal }} {{ $t('wholesaler.OrderOnBehalf.itemUnit') }}</span>
          <button class="page-btn" :disabled="productPage >= productTotalPages - 1" @click="goProductPage(productPage + 1)">{{ $t('wholesaler.OrderOnBehalf.nextPage') }} ›</button>
        </div>
      </div>
      
      <!-- 支付信息 -->
      <div class="section" v-if="selectedCustomer">
        <div class="section-title">{{ $t('wholesaler.OrderOnBehalf.step3PaymentInfo') }}</div>
        
        <!-- 是否已收款 -->
        <div class="payment-received-toggle">
          <label class="toggle-label">
            <input type="checkbox" v-model="paymentReceived" />
            <span class="toggle-icon">{{ paymentReceived ? '✅' : '⬜' }}</span>
            <span>{{ $t('wholesaler.OrderOnBehalf.customerPaid') }}</span>
          </label>
        </div>

        <!-- 直接完成配送 -->
        <div class="delivery-toggle" style="margin-top: 12px;">
          <label class="toggle-label">
            <input type="checkbox" v-model="completeDelivery" />
            <span class="toggle-icon">{{ completeDelivery ? '🚚' : '⬜' }}</span>
            <span>{{ $t('wholesaler.OrderOnBehalf.completeDeliveryDirectly') }}</span>
          </label>
        </div>
        
        <!-- 订单创建时间 -->
        <div class="created-time-section" style="margin-top: 12px;">
          <div class="proof-subtitle">{{ $t('wholesaler.OrderOnBehalf.orderCreateTime') }}</div>
          <div class="datetime-row">
            <el-date-picker
              v-model="orderCreatedAt"
              type="datetime"
              :placeholder="$t('wholesaler.OrderOnBehalf.selectOrderTime')"
              format="yyyy-MM-dd HH:mm"
              value-format="yyyy-MM-ddTHH:mm"
              class="datetime-picker"
            />
            <button class="datetime-now-btn" @click="setNowTime">{{ $t('wholesaler.OrderOnBehalf.now') }}</button>
            <button class="datetime-confirm-btn" @click="confirmOrderTime">{{ $t('wholesaler.OrderOnBehalf.confirm') }}</button>
          </div>
        </div>
        
        <!-- 已收款：选择支付方式 + 上传凭证 -->
        <div v-if="paymentReceived" class="payment-proof-section">
          <div class="proof-subtitle">{{ $t('wholesaler.OrderOnBehalf.selectPaymentMethod') }}</div>
          <div class="payment-options">
            <label 
              v-for="payment in proofPaymentOptions" 
              :key="payment.value"
              :class="['payment-option', { selected: paymentMethod === payment.value }]"
            >
              <input 
                type="radio" 
                :value="payment.value" 
                v-model="paymentMethod"
              />
              <span>{{ payment.label }}</span>
            </label>
          </div>
          
          <div class="proof-subtitle">{{ $t('wholesaler.OrderOnBehalf.uploadPaymentProof') }}</div>
          <div class="proof-upload-area" @click="triggerProofUpload">
            <img v-if="paymentProofUrl" :src="getImageUrl(paymentProofUrl)" class="proof-preview" />
            <div v-else class="proof-placeholder">
              <span class="proof-icon">📎</span>
              <p>{{ $t('wholesaler.OrderOnBehalf.clickToUploadProof') }}</p>
            </div>
          </div>
          <input 
            ref="proofInput" 
            type="file" 
            accept="image/*" 
            style="display:none" 
            @change="handleProofUpload" 
          />
        </div>
        
        <!-- 未收款：默认货到付款 -->
        <div v-else class="cod-hint">
          {{ $t('wholesaler.OrderOnBehalf.codHint') }}
        </div>
      </div>
      
      <!-- 吸底操作栏 -->
      <div class="footer" v-if="selectedCustomer">
        <div class="footer-left">
          <div class="total-price" @click="showPriceDetail = true">
            <span class="total-amount">S$ {{ totalPrice.toFixed(2) }}</span>
            <span class="detail-btn">明细</span>
          </div>
        </div>
        <div class="footer-right">
          <button class="submit-btn" @click="submitOrder(false)" :disabled="submitting">
            {{ submitting ? $t('wholesaler.OrderOnBehalf.submitting') : $t('wholesaler.OrderOnBehalf.submit') }}
          </button>
          <button class="submit-continue-btn" @click="submitOrder(true)" :disabled="submitting">
            {{ submitting ? $t('wholesaler.OrderOnBehalf.submitting') : $t('wholesaler.OrderOnBehalf.submitAndContinue') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 价格明细弹窗 -->
    <div v-if="showPriceDetail" class="dialog-overlay" @click.self="showPriceDetail = false">
      <div class="dialog-box price-detail-dialog">
        <div class="dialog-title">订单明细</div>
        <div class="price-detail-content">
          <div class="detail-stall-info" v-if="selectedCustomer">
            <span class="stall-icon">🏪</span>
            <span class="stall-name">{{ selectedCustomer.shopName || selectedCustomer.customerName || selectedCustomer.name }}</span>
            <span class="stall-count">{{ selectedProductIds.length }} 件商品</span>
          </div>
          <div v-for="product in selectedProducts" :key="product.id" class="price-detail-item">
            <div class="item-info">
              <span class="item-name">{{ product.name }}</span>
              <span class="item-qty">× {{ quantities[product.id] || 0 }}</span>
            </div>
            <div class="item-price">
              <span class="unit-price">S$ {{ (prices[product.id] || 0).toFixed(2) }}</span>
              <span class="item-total">= S$ {{ ((prices[product.id] || 0) * (quantities[product.id] || 0)).toFixed(2) }}</span>
            </div>
          </div>
          <div class="price-detail-footer">
            <span class="footer-label">合计：</span>
            <span class="footer-total">S$ {{ totalPrice.toFixed(2) }}</span>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="modal-btn modal-btn-primary" @click="showPriceDetail = false">关闭</button>
        </div>
      </div>
    </div>

    <!-- 数量选择弹窗 -->
    <div v-if="showQuantityPicker" class="dialog-overlay" @click.self="closeQuantityPicker">
      <div class="dialog-box quantity-picker-dialog">
        <div class="dialog-title">{{ $t('wholesaler.OrderOnBehalf.quantity') }} {{ currentPickerProduct && currentPickerProduct.name ? currentPickerProduct.name : '' }}</div>
        <div class="dialog-content">
          <!-- 滚轮选择区域 -->
          <div class="quantity-picker">
            <div class="picker-highlight"></div>
            <div class="picker-scroll" ref="modalPickerScroll" @scroll="onModalPickerScroll">
              <div class="picker-spacer"></div>
              <div class="picker-item" v-for="qty in getQuantityOptions()" :key="qty">{{ qty }}</div>
              <div class="picker-spacer"></div>
            </div>
          </div>
          <!-- 精确输入区域 -->
          <div class="precise-input-section">
            <label>{{ $t('wholesaler.OrderOnBehalf.exactQuantity') }}：</label>
            <input 
              ref="quantityInput"
              type="text" 
              v-model="customQuantityInput" 
              class="precise-input" 
              @blur="onQuantityBlur"
              :placeholder="$t('wholesaler.OrderOnBehalf.enterExactValue')"
            />
            <span class="stock-unit">{{ currentPickerProduct && currentPickerProduct.unit ? currentPickerProduct.unit : 'kg' }}</span>
          </div>
          <!-- 库存提示 -->
          <div class="stock-hint" v-if="currentPickerProduct">
            {{ $t('wholesaler.OrderOnBehalf.stock') }}：{{ currentPickerProduct.totalStock || 0 }} {{ currentPickerProduct.unit || 'kg' }}
          </div>
        </div>
        <div class="dialog-actions">
          <button class="dialog-cancel-btn" @click="closeQuantityPicker">{{ $t('wholesaler.OrderOnBehalf.cancel') }}</button>
          <button class="dialog-confirm-btn" @click="confirmQuantity">{{ $t('wholesaler.OrderOnBehalf.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- 价格选择弹窗 -->
    <div v-if="showPricePicker" class="dialog-overlay" @click.self="closePricePicker">
      <div class="dialog-box price-picker-dialog">
        <div class="dialog-title">{{ $t('wholesaler.OrderOnBehalf.price') }} {{ currentPickerProduct && currentPickerProduct.name ? currentPickerProduct.name : '' }}</div>
        <div class="dialog-content">
          <!-- 滚轮选择区域 -->
          <div class="quantity-picker">
            <div class="picker-highlight"></div>
            <div class="picker-scroll" ref="pricePickerScroll" @scroll="onPricePickerScroll">
              <div class="picker-spacer"></div>
              <div class="picker-item" v-for="price in getPriceOptions()" :key="price">{{ formatPriceOption(price) }}</div>
              <div class="picker-spacer"></div>
            </div>
          </div>
          <!-- 精确输入区域 -->
          <div class="precise-input-section">
            <label>{{ $t('wholesaler.OrderOnBehalf.exactPrice') }}：</label>
            <input 
              ref="priceInput"
              type="text" 
              v-model="customPriceInput" 
              class="precise-input" 
              @blur="onPriceBlur"
              :placeholder="$t('wholesaler.OrderOnBehalf.enterExactValue')"
            />
            <span class="stock-unit">S$</span>
          </div>
          <!-- 参考价 -->
          <div class="price-reference">
            <div class="price-ref-item">
              <span class="price-ref-label">{{ $t('wholesaler.OrderOnBehalf.referencePrice') }}：</span>
              <span class="price-ref-value">S$ {{ currentPickerProduct ? ((currentPickerProduct.wholesalePrice || currentPickerProduct.minWholesalePrice || currentPickerProduct.marketPrice) || 0).toFixed(2) : '0.00' }}</span>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button class="dialog-cancel-btn" @click="closePricePicker">{{ $t('wholesaler.OrderOnBehalf.cancel') }}</button>
          <button class="dialog-confirm-btn" @click="confirmPrice">{{ $t('wholesaler.OrderOnBehalf.confirm') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { getWholesalerId } from '@/utils/wholesaler';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'OrderOnBehalf',
  data() {
    return {
      customerSearch: '',
      filterRegion: '',
      regions: [],
      customers: [],
      selectedCustomer: null,
      products: [],
      productSearch: '',
      quantities: {},
      prices: {},
      selectedProductIds: [],
      showPriceDetail: false,
      paymentMethod: 'COD',
      paymentReceived: false,
      paymentProofUrl: '',
      completeDelivery: false,
      orderCreatedAt: '',  // 订单创建时间（可手动修改）
      proofPaymentOptions: [
        { label: '💵 现金', value: 'CASH' },
        { label: '📱 扫码支付（PayNow）', value: 'PAYNOW' },
        { label: '🏦 银行转账', value: 'BANK_TRANSFER' },
        { label: '💳 信用卡刷卡支付', value: 'CREDIT_CARD' }
      ],
      submitting: false,
      productsLoading: false,
      showCreateStallDialog: false,
      newStallName: '',
      creatingStall: false,
      // 分页相关
      customerPage: 1,
      customerPageSize: 20,
      customerTotal: 0,
      customerTotalPages: 0,
      customerLoading: false,
      searchTimer: null,
      
      // 数量选择弹窗
      showQuantityPicker: false,
      currentPickerProduct: null,
      customQuantity: 1,
      customQuantityInput: '',
      
      // 价格选择弹窗
      showPricePicker: false,
      customPrice: 0,
      customPriceInput: '',
      
      // 改价标记
      originalPrices: {},
      priceChanged: {},
      
      // 商品搜索下拉
      showProductDropdown: false,
      productSearchResults: [],
      productSearchTimer: null,
      
      // 商品分页
      productPage: 0,
      productPageSize: 20,
      productTotal: 0,
      productTotalPages: 0,
    };
  },
  computed: {
    displayedCustomers() {
      return this.customers || [];
    },
    displayedProducts() {
      if (!this.productSearch.trim()) {
        return this.products;
      }
      // ⭐ 有搜索关键词时，用后端 API 返回的结果（productSearchResults）
      // 避免本地过滤只在前 20 条里找，导致匹配不到
      return this.productSearchResults;
    },
    selectedProducts() {
      return this.products.filter(p => this.selectedProductIds.includes(p.id));
    },
    totalPrice() {
      return this.selectedProducts.reduce((sum, product) => {
        const price = this.prices[product.id] || 0;
        const qty = this.quantities[product.id] || 0;
        return sum + (price * qty);
      }, 0);
    }
  },
  created() {
    this.fetchCustomers();
    this.fetchRegions();
    this.setNowTime();
    // ✅ 页面加载时只获取档口，不获取商品
  },
  methods: {
    getImageUrl,
    getWholesalerId() {
      return getWholesalerId();
    },

    async fetchRegions() {
      try {
        const res = await api.get('/wholesaler/stalls/regions');
        if (Array.isArray(res)) {
          this.regions = res;
        } else if (res && Array.isArray(res.data)) {
          this.regions = res.data;
        } else if (res && Array.isArray(res.items)) {
          this.regions = res.items;
        }
      } catch (error) {
        console.error('加载区域失败:', error);
      }
    },

    async fetchCustomers(page = 1) {
      if (this.customerLoading) return;
      this.customerLoading = true;
      
      try {
        const params = {
          page: page,
          size: this.customerPageSize
        };
        // 单条件搜索：名称、联系人、电话
        if (this.customerSearch && this.customerSearch.trim()) {
          params.keyword = this.customerSearch.trim();
        }
        // 区域筛选
        if (this.filterRegion) {
          params.region = this.filterRegion;
        }
        // 只查询属于当前批发商的档口
        const res = await api.get('/wholesaler/stalls', { params });
        
        if (res && res.items) {
          this.customers = res.items.map(c => {
            // 使用 stallId（实际档口 id）而不是 id（mapping 表 id）
            let customerId = c.stallId || c.id;
            
            // 如果 id 是对象，尝试提取实际值
            if (typeof customerId === 'object' && customerId !== null) {
              customerId = customerId.id || customerId.value || Object.values(customerId)[0];
            }
            
            return {
              ...c,
              id: Number(customerId)
            };
          });
          
          this.customerPage = page;
          this.customerTotal = res.total || 0;
          this.customerTotalPages = res.totalPages || 0;
        }
      } catch (error) {
        console.error('加载档口失败:', error);
        this.customers = [];
      } finally {
        this.customerLoading = false;
      }
    },

    goCustomerPage(page) {
      if (page < 1 || page > this.customerTotalPages) return;
      this.fetchCustomers(page);
    },

    goProductPage(page) {
      if (page < 0 || page >= this.productTotalPages) return;
      const customerId = this.selectedCustomer?.id;
      if (!customerId) return;
      this.fetchProductsForCustomer(customerId, page, false);
    },

    filterCustomers() {
      this.fetchCustomers(1);
    },

    onSearchInput() {
      // 防抖处理，300ms 后自动搜索
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.filterCustomers();
      }, 300);
    },

    onFilterChange() {
      // 筛选条件变化时立即搜索
      this.filterCustomers();
    },

    resetFilters() {
      // 重置所有筛选条件
      this.customerSearch = '';
      this.filterRegion = '';
      this.filterCustomers();
    },

    async createStall() {
      if (!this.newStallName.trim()) return;
      
      this.creatingStall = true;
      try {
        const wholesalerId = this.getWholesalerId();
        const res = await api.post('/wholesaler/stalls/quick-create', {
          name: this.newStallName.trim(),
          wholesalerId: wholesalerId
        });
        
        this.$message.success('档口创建成功');
        this.showCreateStallDialog = false;
        this.newStallName = '';
        
        // 刷新档口列表
        await this.fetchCustomers();
        
        // 自动选中新创建的档口
        if (res && res.id) {
          const newCustomer = this.customers.find(c => c.id === res.id);
          if (newCustomer) {
            this.selectCustomer(newCustomer);
          }
        }
      } catch (error) {
        console.error('创建档口失败:', error);
        this.$message.error('创建档口失败：' + (error.message || '请稍后重试'));
      } finally {
        this.creatingStall = false;
      }
    },

    async selectCustomer(customer) {
      // 先清空商品列表，避免旧数据的 id 对象导致 key 警告
      this.products = [];
      this.selectedCustomer = customer;
      // 不清空已选商品、数量、价格，保持用户之前的选择
      
      // 每次切换档口都要重新加载商品（不同档口商品可能不同）
      // ⭐ 使用 stallId（档口ID），不是 id（mapping表ID）
      await this.fetchProductsForCustomer(customer.stallId || customer.id);
      
      // 每次切换档口都加载上次订单数据，用于填充数量
      this.loadLastOrder(customer.stallId || customer.id);
    },

    // ✅ 加载最近订单并自动填充商品和数量（价格不变）
    async loadLastOrder(customerId) {
      try {
        const wholesalerId = this.getWholesalerId();
        if (!wholesalerId) return;
        
        const res = await api.get('/orders/last-order', {
          params: { customerId, wholesalerId }
        });
        
        if (res && res.hasLastOrder && res.items && res.items.length > 0) {
          // 等待商品列表加载完成
          await this.$nextTick();
          
          let count = 0;
          // 自动填充商品和数量
          res.items.forEach(item => {
            const productId = item.productId;
            // 检查商品是否存在于当前商品列表中
            const product = this.products.find(p => p.id === productId);
            if (product) {
              // 自动勾选
              if (!this.selectedProductIds.includes(productId)) {
                this.selectedProductIds.push(productId);
              }
              // 设置数量
              this.$set(this.quantities, productId, item.quantity || 1);
              // ⭐ 设置价格（使用历史订单价格）
              const historyPrice = item.unitPrice || item.price || 0;
              if (historyPrice > 0) {
                this.$set(this.prices, productId, historyPrice);
                this.$set(this.originalPrices, productId, historyPrice);
                this.$set(this.priceChanged, productId, false);
              }
              count++;
            }
          });
          
          // 提示用户
          if (count > 0) {
            this.$message.success(`已加载上次订单的 ${count} 个商品`);
          }
        }
      } catch (error) {
        console.error('加载最近订单失败:', error);
        // 不阻断流程
      }
    },

    // ✅ 根据档口ID + 批发商ID获取商品（含批发价、市场价、库存）
    async fetchProductsForCustomer(customerId, page = 0, append = false) {
      this.productsLoading = true;
      try {
        const wholesalerId = this.getWholesalerId();
        if (!wholesalerId) {
          console.warn('⚠️ 未获取到批发商ID');
          this.productsLoading = false;
          return;
        }
        const res = await api.get('/customer/products/for-order-on-behalf', {
          params: { wholesalerId, customerId, page, size: this.productPageSize }
        });
        console.log('🔍 商品 API 原始响应:', res);
        let items = [];
        let total = 0;
        let totalPages = 0;
        
        if (res.items) {
          items = res.items;
          total = res.total || 0;
          totalPages = res.totalPages || 0;
        } else if (res.data) {
          if (Array.isArray(res.data)) {
            items = res.data;
          } else if (res.data.items && Array.isArray(res.data.items)) {
            items = res.data.items;
            total = res.data.total || 0;
            totalPages = res.data.totalPages || 0;
          }
        } else if (Array.isArray(res)) {
          items = res;
        }
        
        console.log('🔍 提取的商品列表:', items);
        if (items.length > 0) {
          console.log('🔍 第一个商品对象:', items[0]);
          console.log('🔍 第一个商品的 id:', items[0].id, '类型:', typeof items[0].id);
        }
        
        const newProducts = items.map(item => {
          const productId = Number(item.id);
          console.log('🔍 转换商品 ID:', item.id, '->', productId, '类型:', typeof productId);
          return {
            ...item,
            id: productId,
            quantity: 0
          };
        });
        
        if (append) {
          this.products = [...this.products, ...newProducts];
        } else {
          this.products = newProducts;
        }
        
        this.productTotal = total;
        this.productTotalPages = totalPages;
        this.productPage = page;
        
        console.log('🔍 最终 products 列表:', this.products);
        console.log('🔍 分页信息: page', page, 'total', total, 'totalPages', totalPages);
      } catch (error) {
        console.error('加载商品失败:', error);
        if (!append) {
          this.products = [];
        }
      } finally {
        this.productsLoading = false;
      }
    },

    // 加载更多商品
    async loadMoreProducts() {
      if (this.productsLoading) return;
      if (this.productPage + 1 >= this.productTotalPages) return;
      
      const customerId = this.selectedCustomer?.id;
      if (!customerId) return;
      
      await this.fetchProductsForCustomer(customerId, this.productPage + 1, true);
    },

    // ✅ 处理 URL 传入的 productIds（默认选中）
    handlePreselectedProducts() {
      const productIds = this.$route.query.productIds;
      if (!productIds) return;
      
      const ids = productIds.split(',').map(id => parseInt(id));
      this.selectedProductIds = ids;
      
      // 初始化数量
      ids.forEach(id => {
        const product = this.products.find(p => p.id === id);
        if (product && !this.quantities[id]) {
          this.quantities[id] = product.minOrderQuantity || 1;
        }
      });
    },

    filterProducts() {
      // 保留兼容，不再使用
    },

    onProductSearch() {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(async () => {
        const keyword = this.productSearch.trim();
        if (!keyword) {
          this.productSearchResults = [];
          this.showProductDropdown = false;
          return;
        }
        try {
          const wholesalerId = this.getWholesalerId();
          if (!wholesalerId) return;
          const res = await api.get('/customer/products/for-order-on-behalf', {
            params: { wholesalerId, customerId: this.selectedCustomer?.id, keyword }
          });
          let items = [];
          if (Array.isArray(res)) {
            items = res;
          } else if (res?.items) {
            items = res.items;
          } else if (res?.data) {
            items = Array.isArray(res.data) ? res.data : (res.data.items || []);
          }
          this.productSearchResults = items.map(item => ({
            ...item,
            id: Number(item.id)
          }));
          this.showProductDropdown = this.productSearchResults.length > 0;
        } catch (e) {
          console.error('商品搜索失败:', e);
        }
      }, 300);
    },

    selectSearchProduct(product) {
      // 如果已选，取消选择；否则选中
      const index = this.selectedProductIds.indexOf(product.id);
      if (index === -1) {
        this.toggleProductSelection(product);
      }
      this.showProductDropdown = false;
      
      // ⭐ 把搜索结果合并到主列表，避免清空搜索后商品消失
      const exists = this.products.find(p => p.id === product.id);
      if (!exists) {
        this.products.push({ ...product, quantity: 0 });
      }
      
      this.productSearch = '';
      this.productSearchResults = [];
    },

    toggleProductSelection(product) {
      const index = this.selectedProductIds.indexOf(product.id);
      if (index > -1) {
        this.selectedProductIds.splice(index, 1);
        this.quantities[product.id] = 0;
        this.prices[product.id] = 0;
        this.originalPrices[product.id] = 0;
        this.priceChanged[product.id] = false;
      } else {
        this.selectedProductIds.push(product.id);
        if (!this.quantities[product.id]) {
          this.quantities[product.id] = product.minOrderQuantity || 1;
        }
        // ⭐ 初始化价格（优先用批发价）
        if (!this.prices[product.id]) {
          const originalPrice = product.wholesalePrice || product.minWholesalePrice || product.marketPrice || 0;
          this.prices[product.id] = originalPrice;
          this.originalPrices[product.id] = originalPrice;
          this.priceChanged[product.id] = false;
        }
      }
    },

    /**
     * 生成数量选项（<10 用 0.5 步进，>=10 用 1 步进）
     */
    getQuantityOptions() {
      const options = [];
      const max = 50;
      for (let i = 1; i <= 19; i++) {
        options.push(i * 0.5);
      }
      for (let i = 10; i <= max; i++) {
        options.push(i);
      }
      return options;
    },
    
    /**
     * 打开数量选择弹窗
     */
    openQuantityPicker(product) {
      if (!this.selectedProductIds.includes(product.id)) {
        this.toggleProductSelection(product);
      }
      this.currentPickerProduct = product;
      this.customQuantity = this.quantities[product.id] || product.minOrderQuantity || 1;
      this.customQuantityInput = this.safeNumberToString(this.customQuantity);
      this.showQuantityPicker = true;
      this.$nextTick(() => {
        const el = this.$refs.modalPickerScroll;
        if (el) {
          const qty = this.customQuantity;
          const options = this.getQuantityOptions();
          let bestIndex = 0;
          let bestDiff = Infinity;
          options.forEach((opt, idx) => {
            const diff = Math.abs(opt - qty);
            if (diff < bestDiff) {
              bestDiff = diff;
              bestIndex = idx;
            }
          });
          el.scrollTop = bestIndex * 32;
        }
        // 自动聚焦输入框
        if (this.$refs.quantityInput) {
          this.$refs.quantityInput.focus();
          this.$refs.quantityInput.select();
        }
      });
    },
    
    closeQuantityPicker() {
      this.showQuantityPicker = false;
      this.currentPickerProduct = null;
    },
    
    onModalPickerScroll() {
      if (this._modalScrollTimer) {
        clearTimeout(this._modalScrollTimer);
      }
      this._modalScrollTimer = setTimeout(() => {
        const el = this.$refs.modalPickerScroll;
        if (!el || !this.currentPickerProduct) return;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.round(scrollTop / itemHeight);
        const options = this.getQuantityOptions();
        if (options[index] !== undefined) {
          this.customQuantity = options[index];
          this.customQuantityInput = this.safeNumberToString(options[index]);
        }
      }, 100);
    },
    
    onQuantityBlur() {
      const qty = parseFloat(this.customQuantityInput);
      if (isNaN(qty) || qty <= 0) {
        this.customQuantityInput = this.safeNumberToString(this.customQuantity);
      } else {
        this.customQuantity = qty;
        this.customQuantityInput = this.safeNumberToString(qty);
      }
    },
    
    confirmQuantity() {
      const qty = parseFloat(this.customQuantityInput);
      if (this.currentPickerProduct && !isNaN(qty) && qty > 0) {
        this.$set(this.quantities, this.currentPickerProduct.id, qty);
      }
      this.closeQuantityPicker();
    },
    
    /**
     * 打开价格选择弹窗
     */
    openPricePicker(product) {
      if (!this.selectedProductIds.includes(product.id)) {
        this.toggleProductSelection(product);
      }
      this.currentPickerProduct = product;
      this.customPrice = this.prices[product.id] || product.wholesalePrice || product.minWholesalePrice || product.marketPrice || 0;
      this.customPriceInput = parseFloat((Number(this.customPrice) || 0).toFixed(4)).toString();
      this.showPricePicker = true;
      this.$nextTick(() => {
        const el = this.$refs.pricePickerScroll;
        if (el) {
          const options = this.getPriceOptions();
          const currentPrice = Number(this.customPrice) || 0;
          let currentIndex = options.findIndex(p => Math.abs(p - currentPrice) < 0.0001);
          if (currentIndex < 0) currentIndex = 99;
          el.scrollTop = currentIndex * 32;
        }
        // 自动聚焦输入框
        if (this.$refs.priceInput) {
          this.$refs.priceInput.focus();
          this.$refs.priceInput.select();
        }
      });
    },
    
    /**
     * 生成价格选项（以当前价格为中心，200个选项）
     * 步长 = 当前价格 ÷ 100，抹零到第一个有效数字
     */
    formatPriceOption(price) {
      if (price >= 100) return price.toFixed(0);
      if (price >= 10) return price.toFixed(1);
      return price.toFixed(2);
    },
    
    getPriceStep(price) {
      if (price <= 0) return 0.01;
      if (price < 1) return 0.01;
      if (price < 10) return 0.1;
      if (price < 100) return 0.5;
      if (price < 1000) return 1;
      return 10;
    },
    
    getPriceOptions() {
      const price = Number(this.customPrice) || 0;
      const step = this.getPriceStep(price);
      const options = [];
      for (let i = -99; i <= 100; i++) {
        const optionPrice = price + i * step;
        if (optionPrice >= 0) {
          options.push(parseFloat(optionPrice.toFixed(4)));
        }
      }
      return options;
    },
    
    /**
     * 价格滚轮滚动事件
     */
    onPricePickerScroll() {
      if (this._priceScrollTimer) {
        clearTimeout(this._priceScrollTimer);
      }
      this._priceScrollTimer = setTimeout(() => {
        const el = this.$refs.pricePickerScroll;
        if (!el) return;
        const itemHeight = 32;
        const scrollTop = el.scrollTop;
        const index = Math.round(scrollTop / itemHeight);
        const options = this.getPriceOptions();
        if (options[index] !== undefined) {
          this.customPrice = options[index];
          this.customPriceInput = this.safeNumberToString(options[index]);
        }
      }, 100);
    },
    
    onPriceInput() {
      const val = this.customPriceInput.replace(/[^\d.]/g, '');
      this.customPriceInput = val;
      const price = parseFloat(val);
      if (isNaN(price) || price < 0) return;
      const el = this.$refs.pricePickerScroll;
      if (el) {
        const options = this.getPriceOptions();
        let bestIndex = 0;
        let bestDiff = Infinity;
        options.forEach((opt, idx) => {
          const diff = Math.abs(opt - price);
          if (diff < bestDiff) {
            bestDiff = diff;
            bestIndex = idx;
          }
        });
        el.scrollTop = bestIndex * 32;
      }
    },
    
    /**
     * 安全地将数字转为字符串，避免浮点精度问题
     */
    safeNumberToString(num) {
      if (isNaN(num) || num === null || num === undefined) return '0';
      // 先四舍五入到4位小数，再转字符串
      return parseFloat(Number(num).toFixed(4)).toString();
    },
    
    onPriceBlur() {
      const price = parseFloat(this.customPriceInput);
      if (isNaN(price) || price < 0) {
        this.customPriceInput = this.safeNumberToString(this.customPrice);
      } else {
        this.customPrice = price;
        this.customPriceInput = this.safeNumberToString(price);
      }
    },
    
    closePricePicker() {
      this.showPricePicker = false;
      this.currentPickerProduct = null;
    },
    
    confirmPrice() {
      const priceValue = parseFloat(this.customPriceInput);
      if (this.currentPickerProduct && !isNaN(priceValue) && priceValue >= 0) {
        const productId = this.currentPickerProduct.id;
        this.$set(this.prices, productId, priceValue);
        const originalPrice = Number(this.originalPrices[productId]) || 0;
        const isChanged = Math.abs(priceValue - originalPrice) > 0.001;
        this.$set(this.priceChanged, productId, isChanged);
      }
      this.closePricePicker();
    },

    setNowTime() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      this.orderCreatedAt = `${year}-${month}-${day}T${hours}:${minutes}`;
      this.$message.success('已设置为当前时间');
    },
    confirmOrderTime() {
      if (this.orderCreatedAt) {
        this.$message.success('订单创建时间已确认：' + this.orderCreatedAt.replace('T', ' '));
      } else {
        this.$message.warning('请先选择订单创建时间');
      }
    },

    triggerProofUpload() {
      this.$refs.proofInput.click();
    },

    async handleProofUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      try {
        const { uploadFile } = await import('@/services/api');
        const result = await uploadFile('/files/upload-image', file);
        if (result && result.url) {
          this.paymentProofUrl = result.url;
        }
      } catch (error) {
        console.error('凭证上传失败:', error);
        alert('凭证上传失败：' + error.message);
      } finally {
        event.target.value = '';
      }
    },

    async submitOrder(continueNext) {
      if (this.selectedProductIds.length === 0) {
        alert('请至少选择一个商品');
        return;
      }
      
      if (!this.selectedCustomer) {
        alert('请选择档口');
        return;
      }
      
      const orderItems = this.selectedProductIds
        .filter(id => this.quantities[id] && this.quantities[id] > 0)
        .map(id => {
          const product = this.products.find(p => p.id === id);
          // ⭐ 用手动编辑的价格
          const price = this.prices[id] || product.marketPrice || 0;
          return {
            productId: product.id,
            quantity: this.quantities[id],
            unitPrice: price,
            priceChanged: this.priceChanged[id] || false
          };
        });
      
      if (orderItems.length === 0) {
        alert('请至少填写一个商品的数量');
        return;
      }
      
      this.submitting = true;
      
      try {
        const subtotal = orderItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
        
        const orderPayload = {
          orderType: 'AGENT',
          sourceChannel: 'WHOLESALER',
          wholesalerId: this.getWholesalerId(),
          customerId: this.selectedCustomer.id,
          customerName: this.selectedCustomer.shopName || this.selectedCustomer.customerName || this.selectedCustomer.name,
          customerPhone: this.selectedCustomer.phone,
          packageId: null,
          items: orderItems,
          paymentMethod: this.paymentReceived ? this.paymentMethod : 'COD',
          paymentStatus: this.paymentReceived ? 'paid' : 'unpaid',
          paymentProofUrl: this.paymentProofUrl || null,
          deliveryMethod: 'DELIVERY',
          deliveryAddress: this.selectedCustomer.address || this.selectedCustomer.companyAddress || '',
          deliveryAddressLat: this.selectedCustomer.latitude || null,
          deliveryAddressLng: this.selectedCustomer.longitude || null,
          remark: '',
          subtotal,
          gstAmount: null,
          totalAmount: subtotal,
          skipPriceCheck: true,
          completeDelivery: this.completeDelivery,
          createdAt: this.orderCreatedAt ? this.orderCreatedAt.replace('T', ' ') + ':00' : null
        };
        
        console.log('📦 代客下单提交:', orderPayload);
        
        const response = await api.post('/orders', orderPayload);
        console.log('✅ 订单提交成功:', response);
        
        alert(this.$t('wholesaler.OrderOnBehalf.orderSubmitted'));
        
        if (continueNext) {
          this.selectedCustomer = null;
          this.selectedProductIds = [];
          this.quantities = {};
          this.prices = {};
          this.products = [];
          this.paymentProofUrl = '';
          this.completeDelivery = false;
          // 保留 customerRegion，清空搜索框
          this.customerSearch = '';
          // 滚动到页面顶部
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.$router.push('/wholesaler/orders');
        }
      } catch (error) {
        console.error('提交订单失败:', error);
        const errorMsg = error.message || '提交失败，请稍后重试';
        alert('提交失败：' + errorMsg);
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.order-on-behalf-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 80px;
}

/* 吸顶选择区域 */
.sticky-selection {
  position: sticky;
  top: 0;
  z-index: 101;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 12px rgba(102, 126, 234, 0.3);
  padding: 12px 16px;
  color: white;
}

.sticky-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.sticky-title {
  font-size: 15px;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sticky-title::before {
  content: "🏪";
  font-size: 18px;
}

.sticky-count {
  font-size: 13px;
  color: white;
  background: rgba(255, 255, 255, 0.25);
  padding: 4px 12px;
  border-radius: 16px;
  font-weight: 600;
  backdrop-filter: blur(10px);
}

.sticky-products {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 80px;
  overflow-y: auto;
}

.sticky-product-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.95);
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
}

.sticky-product-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.sticky-product-name {
  color: #333;
  font-weight: 500;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sticky-product-qty {
  color: #999;
  font-size: 12px;
  font-weight: 500;
}

.sticky-product-price {
  color: #ff4d4f;
  font-weight: 700;
  font-size: 13px;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 100;
}

.content {
  padding: 16px;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.create-stall-btn {
  padding: 6px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.create-stall-btn:active {
  opacity: 0.8;
}

/* 弹窗样式 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-box {
  background: white;
  border-radius: 12px;
  width: 85%;
  max-width: 360px;
  overflow: hidden;
}

.dialog-title {
  padding: 16px;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  border-bottom: 1px solid #eee;
}

.dialog-content {
  padding: 20px 16px;
}

.dialog-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
}

.dialog-input:focus {
  border-color: #667eea;
}

.dialog-actions {
  display: flex;
  gap: 12px;
  padding: 0 16px 16px;
}

.dialog-cancel-btn,
.dialog-confirm-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.dialog-cancel-btn {
  background: #f5f5f5;
  color: #666;
}

.dialog-confirm-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.dialog-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.search-input,
.region-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
}

.search-input:focus,
.region-select:focus {
  border-color: #667eea;
}

/* 多条件筛选样式 */
.filter-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-row {
  display: flex;
  gap: 8px;
}

.filter-input,
.filter-select {
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  background: white;
}

.filter-input:focus,
.filter-select:focus {
  border-color: #667eea;
}

.filter-buttons {
  display: flex;
  gap: 8px;
}

.search-btn,
.reset-btn {
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.search-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.search-btn:active {
  opacity: 0.8;
}

.reset-btn {
  background: #f5f5f5;
  color: #666;
}

.reset-btn:active {
  background: #e0e0e0;
}

.customer-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.customer-selector {
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.customer-selector.selected {
  border-color: #667eea;
  background: #f0f0ff;
}

.customer-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.customer-info {
  font-size: 12px;
  color: #999;
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 12px;
  padding: 8px 0;
}

.page-btn {
  padding: 6px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  font-size: 12px;
  color: #999;
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.product-item-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-name-full {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  word-break: break-all;
}

.product-item-bottom {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 28px;
}

.price-ref-line {
  font-size: 12px;
  color: #999;
  margin-right: auto;
}

.price-input-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 6px 10px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.price-input-wrap:hover:not(.disabled) {
  background: #f0f0f0;
  border-color: #667eea;
}

.price-input-wrap.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.price-prefix {
  font-size: 12px;
  color: #666;
}

.price-display {
  font-size: 14px;
  font-weight: 600;
  color: #f5576c;
  min-width: 50px;
  text-align: center;
}

.quantity-display-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 6px 10px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.quantity-display-wrap:hover:not(.disabled) {
  background: #f0f0f0;
  border-color: #667eea;
}

.quantity-display-wrap.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-display {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  min-width: 40px;
  text-align: center;
}

.edit-arrow {
  font-size: 10px;
  color: #999;
}

.unit {
  font-size: 12px;
  color: #666;
}

.price-ref {
  font-size: 11px;
  color: #999;
  font-weight: 400;
}

.product-checkbox {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.product-emoji {
  font-size: 24px;
  width: 36px;
  text-align: center;
}

.product-details {
  flex: 1;
  min-width: 0;
}

.product-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
  line-height: 1.4;
}

.product-price {
  font-size: 13px;
  color: #667eea;
  font-weight: 600;
}

.price-original {
  text-decoration: line-through;
  color: #999;
  font-weight: 400;
  font-size: 12px;
  margin-right: 4px;
}

.price-wholesale {
  color: #f5576c;
  font-size: 15px;
}

.price-unit {
  font-weight: 400;
  color: #666;
  font-size: 12px;
}

/* 数量选择弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999 !important;
}

.quantity-picker-dialog {
  max-width: 400px;
}

.quantity-picker {
  position: relative;
  width: 100%;
  height: 160px;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.picker-highlight {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 32px;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0.15;
  border-radius: 4px;
  pointer-events: none;
  z-index: 2;
}

.picker-scroll {
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.picker-scroll::-webkit-scrollbar {
  display: none;
}

.picker-item {
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 16px;
  color: #666;
  scroll-snap-align: center;
  user-select: none;
}

.picker-spacer {
  height: 64px;
  flex-shrink: 0;
}

/* 精确输入区域 */
.precise-input-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 8px;
}

.precise-input-section label {
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
  white-space: nowrap;
}

.precise-input {
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid #667eea;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-align: center;
  outline: none;
}

.precise-input:focus {
  border-color: #764ba2;
  box-shadow: 0 0 0 2px rgba(102,126,234,0.15);
}

.stock-hint {
  font-size: 12px;
  color: #999;
  padding: 4px 0;
}

.stock-unit {
  font-size: 13px;
  color: #999;
  flex-shrink: 0;
}

.quantity-picker-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
}

.modal-btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s;
}

.modal-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-btn:hover {
  opacity: 0.9;
}

/* 价格选择弹窗 */
.price-picker-dialog {
  max-width: 400px;
}

.price-input-section {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 12px;
}

.price-input-section label {
  font-size: 16px;
  font-weight: 600;
  color: #666;
}

.price-input {
  flex: 1;
  padding: 8px 12px;
  border: 1.5px solid #667eea;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 600;
  color: #f5576c;
  text-align: center;
  outline: none;
}

.price-input:focus {
  border-color: #667eea;
}

.price-unit {
  font-size: 13px;
  color: #999;
}

.price-reference {
  padding: 12px;
  background: #fff3e0;
  border-radius: 8px;
}

.price-ref-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price-ref-label {
  font-size: 13px;
  color: #666;
}

.price-ref-value {
  font-size: 15px;
  font-weight: 600;
  color: #f5576c;
}

.price-picker-modal .modal-footer {
  display: flex;
  justify-content: flex-end;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.loading-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.loading-spinner {
  font-size: 32px;
  margin-bottom: 8px;
}

.payment-received-toggle {
  margin-bottom: 12px;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}

.toggle-icon {
  font-size: 20px;
}

.payment-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.payment-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
}

.payment-option.selected {
  border-color: #667eea;
  background: #f0f0ff;
}

.proof-upload-area {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 16px;
}

.proof-preview {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
}

.proof-placeholder {
  color: #999;
}

.proof-icon {
  font-size: 32px;
  display: block;
  margin-bottom: 8px;
}

.proof-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.cod-hint {
  background: #fff3e0;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #e65100;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 12px 16px;
  display: flex;
  gap: 12px;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
  z-index: 100;
  align-items: center;
}

.footer-left {
  flex: 1;
}

.total-price {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f5f5f5;
  transition: background 0.2s;
}

.total-price:hover {
  background: #e8e8e8;
}

.total-label {
  font-size: 14px;
  color: #666;
}

.total-amount {
  font-size: 18px;
  font-weight: 600;
  color: #ff4d4f;
}

.detail-btn {
  font-size: 13px;
  color: #667eea;
  background: #f0f0ff;
  padding: 4px 10px;
  border-radius: 4px;
  margin-left: 4px;
  font-weight: 500;
}

.detail-icon {
  font-size: 14px;
  margin-left: 4px;
}

.footer-right {
  display: flex;
  gap: 8px;
}

.submit-btn,
.submit-continue-btn {
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.submit-continue-btn {
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
}

.submit-btn:disabled,
.submit-continue-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 价格明细弹窗 */
.price-detail-dialog {
  max-width: 500px;
  width: 90%;
}

.price-detail-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

.detail-stall-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 14px;
}

.detail-stall-info .stall-icon {
  font-size: 18px;
}

.detail-stall-info .stall-name {
  font-weight: 600;
  flex: 1;
}

.detail-stall-info .stall-count {
  background: rgba(255, 255, 255, 0.25);
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.price-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.price-detail-item:last-child {
  border-bottom: none;
}

.item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.item-qty {
  font-size: 12px;
  color: #999;
}

.item-price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.unit-price {
  font-size: 13px;
  color: #666;
}

.item-total {
  font-size: 14px;
  color: #ff4d4f;
  font-weight: 600;
}

.price-detail-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  margin: 16px;
}

.footer-label {
  font-size: 15px;
  color: #666;
  font-weight: 500;
}

.footer-total {
  font-size: 20px;
  color: #ff4d4f;
  font-weight: 700;
}

/* 商品搜索下拉 */
.product-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.product-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
}

.product-option:last-child {
  border-bottom: none;
}

.product-option:active {
  background: #f0f7ff;
}

.option-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  flex: 1;
}

.option-sku {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
}

/* 已选商品区域 */
.selected-products-section {
  background: #f0f7ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.selected-title {
  font-size: 14px;
  font-weight: 600;
  color: #409EFF;
  margin-bottom: 8px;
}

.selected-products-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.selected-product-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.selected-product-name {
  font-size: 13px;
  color: #333;
  font-weight: 500;
}

.selected-product-price {
  font-size: 12px;
  color: #666;
}

.btn-remove-selected {
  background: none;
  border: none;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
}

.btn-remove-selected:active {
  background: #f0f0f0;
  color: #f56c6c;
}

.datetime-row {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 6px;
}

.datetime-picker {
  flex: 1;
}

.datetime-picker .el-input__inner {
  height: 40px;
  line-height: 40px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 14px;
}

.datetime-picker .el-input__inner:focus {
  border-color: #409EFF;
}

.datetime-confirm-btn {
  flex-shrink: 0;
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.datetime-confirm-btn:active {
  opacity: 0.8;
}

.datetime-now-btn {
  flex-shrink: 0;
  padding: 10px 16px;
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.datetime-now-btn:active {
  opacity: 0.8;
}
</style>
